import { createRouter, createWebHistory } from "vue-router";
import { isAnalyticsEnabled, trackEvent } from '../services/analytics';
import { PAGE_VIEW } from '../services/analytics-events';
import { useAuthStore } from "../store/authStore";
import { canAccessKenyaTaxCalculator } from "../utils/locationDetection";

// Lazy load route components for better performance
const HomePage = () => import("../views/Home.vue");
const TaxCalculator = () => import("../views/TaxCalculator.vue");
const TaxCalculatorHubView = () => import("../views/TaxCalculatorHubView.vue");
const BillingGenerator = () => import("../views/BillingGenerator.vue");
const Settings = () => import("../views/Settings.vue");
const Profile = () => import("../views/Profile.vue");
const DashboardView = () => import("../views/Dashboard.vue");
const InvoiceCreate = () => import("../views/InvoiceCreate.vue");
const PublicInvoiceView = () => import("../views/PublicInvoiceView.vue");
const TaxGuide = () => import("../views/TaxGuide.vue");
const BlogPage = () => import("../views/Blog.vue");
const Pricing = () => import("../views/Pricing.vue");
const ProjectPitch = () => import("../views/planner/ProjectPitch.vue");
const DesignToolView = () => import("../views/planner/DesignToolView.vue");
const AuthPage = () => import("../views/AuthPage.vue");
const EmailVerification = () => import("../views/EmailVerification.vue");
const ForgotPassword = () => import("../views/ForgotPassword.vue");
const LoginPage = () => import("../views/LoginPage.vue");

const routes = [
  {
    path: "/auth",
    name: "AuthPage",
    component: AuthPage,
    meta: { requiresGuest: true },
  },
  {
    path: "/verify-email",
    name: "EmailVerification",
    component: EmailVerification,
    meta: { requiresAuth: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: { requiresGuest: true },
  },
  { path: "/login", redirect: "/auth" },
  {
    path: "/",
    name: "HomePage",
    component: HomePage,
    meta: {
      title: "SanaaPesa - Global Invoice & Proposal Creator",
      description: "Create professional invoices and proposals worldwide. Global business tools with multi-currency support for freelancers and businesses.",
    },
  },
  {
    path: "/project-pitch",
    name: "ProjectPitch",
    component: ProjectPitch,
    meta: {
      title: "Proposal Creator - Design Winning Project Proposals",
      description: "Create compelling project proposals and business quotes. Professional templates with drag-and-drop design tools for global businesses.",
    },
  },
  {
    path: "/pricing",
    name: "Pricing",
    component: Pricing,
    meta: {
      title: "Pricing - Global Invoice & Proposal Tools",
      description: "Simple, transparent pricing for professional invoice and proposal tools. Multi-currency support with global payment integration.",
    },
  },
  {
    path: "/send-invoice",
    name: "SendInvoice",
    component: BillingGenerator,
    meta: {
      title: "Invoice Generator - Create Professional Invoices",
      description: "Free global invoice generator with multi-currency support. Create professional invoices instantly with customizable templates and payment integration.",
    },
  },
  {
    path: "/tax-helper",
    name: "TaxHelper",
    component: TaxCalculatorHubView,
    meta: {
      title: "Invoices and proposals",
      description:
        "Choose between Simple or Multi-Income tax calculators for accurate Kenya tax calculations",
      requiresKenyaAccess: true,
    },
  },
  {
    path: "/tax-calculator",
    name: "TaxCalculator",
    component: TaxCalculator,
    meta: {
      title: "Employment Tax Calculator",
      description:
        "Quick and easy tax calculation for employed individuals in Kenya with P9 form generation",
      requiresKenyaAccess: true,
    },
  },
  {
    path: "/multi-income-calculator",
    name: "MultiIncomeCalculator",
    component: () => import("../views/MultiIncomeCalculatorView.vue"),
    meta: {
      title: "Multi-Income Tax Calculator",
      description:
        "Calculate taxes for multiple income sources including employment and freelance work",
      requiresKenyaAccess: true,
    },
  },

  // Legacy redirects
  { path: "/billing-planner", redirect: "/project-pitch" },
  { path: "/billing", redirect: "/send-invoice" },

  // Dashboard & Profile
  { path: "/dashboard", name: "Dashboard", component: DashboardView, meta: { requiresAuth: true } },
  { path: "/profile", name: "Profile", component: Profile, meta: { requiresAuth: true } },
  // User settings (requires authentication)
  { path: "/settings", name: "Settings", component: Settings, meta: { requiresAuth: true } },

  // Invoice
  {
    path: "/invoice/create",
    name: "InvoiceCreate",
    component: InvoiceCreate,
    meta: {
      title: "Create Invoice - Professional Invoice Maker",
      description: "Create professional invoices instantly. Global invoice generator with customizable templates, multi-currency support, and payment integration.",
    },
  },

  // Public invoice view (hosted token link)
  {
    path: "/invoice/view",
    name: "PublicInvoiceView",
    component: PublicInvoiceView,
    meta: {
      title: "View Invoice",
      description: "Public view for a shared invoice link",
    },
  },

  // Content pages
  {
    path: "/tax-guide",
    name: "TaxGuide",
    component: TaxGuide,
    meta: {
      title: "Kenya Tax Guide - PAYE, NHIF, NSSF Information",
      description: "Comprehensive guide to Kenya tax system including PAYE, NHIF, NSSF, and tax deductions for Kenyan employees and businesses.",
    },
  },
  {
    path: "/blog",
    name: "Blog",
    component: BlogPage,
    meta: {
      title: "Business Blog - Invoice & Proposal Tips",
      description: "Expert tips for creating professional invoices, winning proposals, and growing your business. Global business advice for freelancers and entrepreneurs.",
    },
  },

  // Design tool (new proposal editor)
  {
    path: "/design-tool",
    name: "DesignTool",
    component: DesignToolView,
    meta: {
      title: "Proposal Design Tool - Drag & Drop Proposal Builder",
      description: "Advanced drag-and-drop proposal designer with live preview. Create stunning business proposals and project quotes with professional templates.",
    },
  },

  // Keep legacy edit-proposal redirect to design tool
  { path: "/edit-proposal/:id", redirect: "/design-tool" },

  // Backward compatibility for old login view
  { path: "/login-old", name: "LoginPage", component: LoginPage, meta: { requiresGuest: true } },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || "/"),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  if (!authStore.isAuthInitialized) await authStore.initAuth();

  const requiresAuth = to.matched.some((r) => r.meta?.requiresAuth);
  const requiresGuest = to.matched.some((r) => r.meta?.requiresGuest);
  const requiresKenyaAccess = to.matched.some((r) => r.meta?.requiresKenyaAccess);
  const isAuthenticated = authStore.isAuthenticated;

  // Check authentication requirements
  if (requiresAuth && !isAuthenticated) {
    const returnUrl = to.fullPath;
    return next(`/auth?returnUrl=${encodeURIComponent(returnUrl)}`);
  }
  if (requiresGuest && isAuthenticated) return next("/");

  // Check Kenya access requirements for tax calculator routes
  if (requiresKenyaAccess) {
    try {
      const hasKenyaAccess = await canAccessKenyaTaxCalculator();
      if (!hasKenyaAccess) {
        // Redirect to home with a query parameter to show the location message
        return next("/?accessDenied=kenya-tax&requestedRoute=" + encodeURIComponent(to.fullPath));
      }
    } catch (error) {
      console.warn("Location access check failed:", error);
      // Continue to route if check fails to avoid blocking legitimate users
    }
  }

  return next();
});

// Auto-track page views after successful navigation
router.afterEach((to, from) => {
  try {
    if (!isAnalyticsEnabled()) return
    const pagePath = to.fullPath || to.path || to.name
    trackEvent(PAGE_VIEW, {
      page_title: to.meta?.title || to.name,
      page_path: pagePath,
      from: from.fullPath || from.path || from.name,
    })
  } catch (err) {
    // Do not let analytics failures affect routing
    console.warn('Failed to track page view:', err)
  }
})

export default router;
